// src/pages/ConfigPage.jsx
// Admin Config — Manage team permission configurations

import React, { useState, useEffect } from 'react';
import { listTeamConfigs, saveTeamConfig, deleteTeamConfig } from '../services/api';

// Master permission list (same as main portal)
const ALL_PERMISSIONS = [
    { id: 'base_pi', name: 'Base PI', riskType: 'LRP' },
    { id: 'action_widget', name: 'Action Widget', riskType: 'HRP' },
    { id: 'case_locker', name: 'Case Locker', riskType: 'MRP' },
    { id: 'create_investigations', name: 'Create Investigations', riskType: 'MRP' },
    { id: 'bulk_asin', name: 'Bulk ASIN', riskType: 'HRP' },
    { id: 'bulk_merchant', name: 'Bulk Merchant', riskType: 'HRP' },
    { id: 'bulk_email_vendor', name: 'Bulk Email Vendor', riskType: 'MRP' },
    { id: 'peeknow', name: 'Peeknow', riskType: 'MRP' },
    { id: 'peeknow_resolved', name: 'Peeknow Resolved Tasks', riskType: 'MRP' },
    { id: 'uow_history', name: 'UoW History Widget', riskType: 'LRP' },
    { id: 'secure_link_nemo', name: 'Secure Link - Nemo', riskType: 'MRP' },
    { id: 'standalone_ccp_launch', name: 'Standalone CCP Launch', riskType: 'MRP' },
    { id: 'ps_appeals_bulk_tools', name: 'PS Appeals Bulk Tools', riskType: 'MRP' },
    { id: 'bulk_sam_action', name: 'Bulk SAM Action', riskType: 'HRP' },
    { id: 'bulk_operations', name: 'Bulk Operations', riskType: 'HRP' },
    { id: 'beta_access', name: 'Beta Access', riskType: 'LRP' },
    { id: 'unfettered_search', name: 'Unfettered Search (UFS)', riskType: 'CRP' },
    { id: 'routing_rcc', name: 'Routing and RCC Permissions', riskType: 'LRP' },
    { id: 'routing_rcc_view', name: 'Routing and RCC - View Only', riskType: 'LRP' },
    { id: 'qct_routing_admin', name: 'QCT and Routing Admin', riskType: 'LRP' },
    { id: 'rcc_debug', name: 'RCC Debug Tool', riskType: 'LRP' },
    { id: 'admin_view_config', name: 'Admin View Config', riskType: 'LRP' },
    { id: 'auditor_central', name: 'Auditor Central Admin', riskType: 'LRP' },
];

const RISK_COLORS = {
    LRP: { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
    MRP: { bg: '#e0f2fe', color: '#0073bb', border: '#7dd3fc' },
    HRP: { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
    CRP: { bg: '#ffeef0', color: '#d13212', border: '#ffc1c8' },
};

const ConfigPage = ({ userAlias }) => {
    const [teams, setTeams] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingTeam, setEditingTeam] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');

    // Form state for editing
    const [formTeamLabel, setFormTeamLabel] = useState('');
    const [formFunction, setFormFunction] = useState('');
    const [formOrgName, setFormOrgName] = useState('');
    const [formOrgAliases, setFormOrgAliases] = useState('');
    const [formApprovalLink, setFormApprovalLink] = useState('');
    const [formPermissions, setFormPermissions] = useState([]);
    const [expandedRiskType, setExpandedRiskType] = useState(null);
    const [permSearch, setPermSearch] = useState('');

    useEffect(() => { loadTeams(); }, []);

    const loadTeams = async () => {
        setIsLoading(true);
        setError('');
        try {
            const result = await listTeamConfigs();
            setTeams(result.teams || []);
        } catch (err) {
            setError('Failed to load configs: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const startNewTeam = () => {
        setEditingTeam('new');
        setFormTeamLabel('');
        setFormFunction('');
        setFormOrgName('');
        setFormOrgAliases('');
        setFormApprovalLink('');
        setFormPermissions([]);
        setSaveMessage('');
    };

    const startEditTeam = (team) => {
        setEditingTeam(team.teamLabel);
        setFormTeamLabel(team.teamLabel);
        setFormFunction(team.functionDropdown || '');
        setFormOrgName(team.orgName || '');
        setFormOrgAliases((team.orgAliases || []).join(', '));
        setFormApprovalLink(team.approvalLink || '');
        setFormPermissions(team.permissions || []);
        setSaveMessage('');
    };

    const cancelEdit = () => {
        setEditingTeam(null);
        setSaveMessage('');
    };

    const togglePermission = (permId) => {
        setFormPermissions(prev => {
            const exists = prev.find(p => p.id === permId);
            if (exists) return prev.filter(p => p.id !== permId);
            return [...prev, { id: permId, useCase: '' }];
        });
    };

    const updateUseCase = (permId, useCase) => {
        setFormPermissions(prev => prev.map(p => p.id === permId ? { ...p, useCase } : p));
    };

    const handleSave = async () => {
        if (!formTeamLabel.trim()) { alert('Team name is required'); return; }
        if (!formFunction) { alert('Function is required'); return; }
        if (!formOrgName.trim()) { alert('Org Name is required'); return; }
        setIsSaving(true);
        setSaveMessage('');
        try {
            await saveTeamConfig({
                teamLabel: formTeamLabel.trim(),
                functionDropdown: formFunction,
                orgName: formOrgName,
                orgAliases: formOrgAliases.split(',').map(s => s.trim()).filter(Boolean),
                approvalLink: formApprovalLink.trim(),
                permissions: formPermissions,
                active: true,
                updatedBy: userAlias || ''
            });
            setSaveMessage('✅ Saved successfully!');
            setEditingTeam(null);
            loadTeams();
        } catch (err) {
            setSaveMessage('❌ Error: ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (teamLabel) => {
        if (!window.confirm(`Delete config for "${teamLabel}"?`)) return;
        try {
            await deleteTeamConfig(teamLabel);
            loadTeams();
        } catch (err) {
            alert('Delete failed: ' + err.message);
        }
    };

    // ════════════════════════════════════════
    // RENDER: Edit/Create Form
    // ════════════════════════════════════════
    if (editingTeam) {
        return (
            <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
                <button onClick={cancelEdit} style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                    ← Back to List
                </button>

                <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px' }}>
                    {editingTeam === 'new' ? '➕ Add New Team' : `✏️ Edit: ${editingTeam}`}
                </h2>

                {/* Team Name */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Team Name *</label>
                    <input type="text" value={formTeamLabel}
                        onChange={(e) => setFormTeamLabel(e.target.value)}
                        placeholder="e.g., PoE International"
                        disabled={editingTeam !== 'new'}
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', backgroundColor: editingTeam !== 'new' ? '#f3f4f6' : '#fff' }} />
                </div>

                {/* Function & Org */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Function</label>
                        <select value={formFunction} onChange={(e) => setFormFunction(e.target.value)}
                            style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }}>
                            <option value="">-- Select Function --</option>
                            <option value="Operations Team">Operations Team</option>
                            <option value="Tech Team">Tech Team</option>
                            <option value="Support Function">Support Function</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Org Name</label>
                        <input type="text" value={formOrgName}
                            onChange={(e) => setFormOrgName(e.target.value)}
                            placeholder="e.g., TSE International"
                            style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                    </div>
                </div>

                {/* Org Aliases (valid department names) */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Valid Department Names (comma-separated)</label>
                    <input type="text" value={formOrgAliases}
                        onChange={(e) => setFormOrgAliases(e.target.value)}
                        placeholder="e.g., POE-VAR, TSE Int'l Program-VAR, TSE International"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>Users whose department matches any of these will not trigger an org mismatch warning.</div>
                </div>

                {/* Approval Link */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '6px' }}>Leadership Approval Link</label>
                    <input type="text" value={formApprovalLink}
                        onChange={(e) => setFormApprovalLink(e.target.value)}
                        placeholder="https://approvals.amazon.com/approval/..."
                        style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px' }} />
                </div>

                {/* Permissions */}
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontWeight: '600', fontSize: '13px', marginBottom: '10px' }}>
                        Approved Permissions ({formPermissions.length} selected)
                    </label>

                    {/* Search */}
                    <input type="text" value={permSearch}
                        onChange={(e) => setPermSearch(e.target.value)}
                        placeholder="Search permissions..."
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', marginBottom: '12px' }} />

                    {['LRP', 'MRP', 'HRP', 'CRP'].map(riskType => {
                        const permsOfType = ALL_PERMISSIONS.filter(p => p.riskType === riskType)
                            .filter(p => !permSearch || p.name.toLowerCase().includes(permSearch.toLowerCase()));
                        const rc = RISK_COLORS[riskType];
                        const isExpanded = expandedRiskType === riskType;
                        const selectedCount = formPermissions.filter(fp => {
                            const perm = ALL_PERMISSIONS.find(ap => ap.id === fp.id);
                            return perm && perm.riskType === riskType;
                        }).length;

                        return (
                            <div key={riskType} style={{ marginBottom: '12px', border: '1px solid #e2e6ea', borderRadius: '8px', overflow: 'hidden' }}>
                                <div
                                    onClick={() => setExpandedRiskType(isExpanded ? null : riskType)}
                                    style={{ padding: '10px 14px', background: '#f8f9fa', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', justifyContent: 'space-between' }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', backgroundColor: rc.bg, color: rc.color, border: `1px solid ${rc.border}` }}>{riskType}</span>
                                        {riskType === 'LRP' && '— Low Risk (180 days, no checks)'}
                                        {riskType === 'MRP' && '— Medium Risk (180 days, Base PI check)'}
                                        {riskType === 'HRP' && '— High Risk (120 days, Base PI + Inactivity)'}
                                        {riskType === 'CRP' && '— Critical Risk (120 days, full UFS validation)'}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {selectedCount > 0 && (
                                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '700', background: '#166534', color: '#fff' }}>
                                                {selectedCount} selected
                                            </span>
                                        )}
                                        <span style={{ fontSize: '12px', color: '#666' }}>{isExpanded ? '▼' : '▶'} {permsOfType.length}</span>
                                    </div>
                                </div>
                                {isExpanded && (
                                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                        {permsOfType.map(perm => {
                                            const isSelected = formPermissions.some(p => p.id === perm.id);
                                            const permConfig = formPermissions.find(p => p.id === perm.id);
                                            return (
                                                <div key={perm.id} style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0', background: isSelected ? '#f0fdf4' : '#fff' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <input type="checkbox" checked={isSelected} onChange={() => togglePermission(perm.id)}
                                                            style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                                                        <span style={{ fontWeight: '500', fontSize: '14px' }}>{perm.name}</span>
                                                    </div>
                                                    {isSelected && (
                                                        <div style={{ marginTop: '8px', marginLeft: '26px' }}>
                                                            <input type="text" value={permConfig?.useCase || ''}
                                                                onChange={(e) => updateUseCase(perm.id, e.target.value)}
                                                                placeholder="Use case (auto-populates for end user)..."
                                                                style={{ width: '100%', padding: '6px 10px', borderRadius: '5px', border: '1px solid #ddd', fontSize: '12px' }} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Save */}
                {saveMessage && <div style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>{saveMessage}</div>}
                <button onClick={handleSave} disabled={isSaving}
                    style={{ padding: '12px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000', width: '100%' }}>
                    {isSaving ? '⏳ Saving...' : '💾 Save Configuration'}
                </button>
            </div>
        );
    }

    // ════════════════════════════════════════
    // RENDER: Team List
    // ════════════════════════════════════════
    return (
        <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>⚙️ T178 Team Permission Config</h2>
                <button onClick={startNewTeam}
                    style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                    ➕ Add Team
                </button>
            </div>

            {error && <div style={{ padding: '12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', color: '#d13212', marginBottom: '16px' }}>❌ {error}</div>}

            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>
            ) : teams.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>📭</div>
                    <p>No teams configured yet. Click "Add Team" to get started.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {teams.map(team => (
                        <div key={team.teamLabel} style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e2e6ea', background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ fontWeight: '700', fontSize: '15px', color: '#333' }}>{team.teamLabel}</div>
                                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                    {team.permissionCount || 0} permissions · Updated by {team.updatedBy || '—'} · {team.updatedAt ? new Date(team.updatedAt).toLocaleDateString() : '—'}
                                </div>
                                {team.approvalLink && (
                                    <a href={team.approvalLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#0073bb' }}>
                                        🔗 Approval Link ↗
                                    </a>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => startEditTeam(team)}
                                    style={{ padding: '6px 12px', borderRadius: '5px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #0073bb', background: '#f0f8ff', color: '#0073bb' }}>
                                    ✏️ Edit
                                </button>
                                <button onClick={() => handleDelete(team.teamLabel)}
                                    style={{ padding: '6px 12px', borderRadius: '5px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #d13212', background: '#ffeef0', color: '#d13212' }}>
                                    🗑️ Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ConfigPage;
